import quickFetch from 'quick-fetch';
import { http, noAuthApiClient } from '../http';
import { dataFetchingClient, registerMutation, registerQuery, useMutation, useQuery } from '../../utils/DataFetchingClient';
import { BASE_URI_LC } from '../../constants/ApiConstants';
import { BROCHURE_URL_BY_LANGUAGE } from '../../constants/AchievementConstants';
import { AWARD_VISIBILITIES } from '../../constants/TracksConstants';
import getLang from 'I18n/utils/getLang';
import { useCallback, useMemo } from 'react';
import { updateCache } from '../../utils/DataFetchingClientCache';
import produce from 'immer';
import { create } from 'zustand';
export const ACHIEVEMENTS_API = `${BASE_URI_LC}/achievements`;

const publicQuickFetchOrGet = url => {
  const earlyProgress = quickFetch.getRequestStateByName(url);

  if (earlyProgress) {
    return new Promise((resolve, reject) => {
      earlyProgress.whenFinished(result => {
        resolve(result);
        quickFetch.removeEarlyRequest(url);
      });
      earlyProgress.onError(xhr => {
        if (xhr.status === 401) reject(xhr);else {
          resolve(noAuthApiClient.get(url));
        }
        quickFetch.removeEarlyRequest(url);
      });
    });
  }

  return noAuthApiClient.get(url);
};

export const denormalizePublicAchievementResponse = response => {
  const denormalizedAchievements = {};
  Object.keys(response.achievements).forEach(publicId => {
    const achievement = response.achievements[publicId];
    const user = response.users[achievement.userId];
    const track = response.tracks[achievement.tracks[0]];
    const lessons = response.lessons;
    denormalizedAchievements[publicId] = Object.assign({}, achievement, {}, user, {
      tracks: [Object.assign({
        id: achievement.tracks[0].toString()
      }, track, {
        lessons: track.lessons.map(lessonId => Object.assign({
          id: lessonId.toString()
        }, lessons[lessonId]))
      })]
    });
  });
  return denormalizedAchievements;
};
export const GET_ALL_ACHIEVEMENTS = registerQuery({
  fieldName: 'allAchievements',
  args: [],

  fetcher() {
    return http.get(ACHIEVEMENTS_API).then(response => denormalizePublicAchievementResponse(response));
  }

});
export const GET_ACHIEVEMENT_BY_PUBLIC_ID = registerQuery({
  fieldName: 'achievementByPublicId',
  args: ['publicId'],

  fetcher({
    publicId
  }) {
    return publicQuickFetchOrGet(`${ACHIEVEMENTS_API}/public/${publicId}`).then(response => denormalizePublicAchievementResponse(response)[publicId]);
  }

});
export const CREATE_ACHIEVEMENT = registerMutation({
  fieldName: 'createAchievement',
  args: ['firstName', 'lastName', 'email', 'language', 'urlSlug', 'visibilityType', 'trackId'],

  fetcher({
    firstName,
    lastName,
    email,
    language,
    urlSlug,
    visibilityType,
    trackId
  }) {
    return http.post(ACHIEVEMENTS_API, {
      data: {
        achievementType: 'SINGLE',
        visibilityType,
        tracks: [trackId],
        firstName,
        lastName,
        email,
        metadata: {
          [language]: {
            urlSlug
          }
        }
      }
    }).then(response => denormalizePublicAchievementResponse(response));
  }

});
export const UPDATE_ACHIEVEMENT = registerMutation({
  fieldName: 'updateAchievement',
  args: ['achievement', 'metadata', 'visibility'],

  fetcher({
    achievement,
    metadata,
    visibility
  }) {
    const putData = {
      achievementType: achievement.achievementType,
      metadata: metadata || achievement.metadata,
      private: visibility ? visibility !== AWARD_VISIBILITIES.PUBLIC : achievement.private,
      publicId: achievement.publicId,
      tracks: achievement.tracks.map(track => parseInt(track.id, 10)),
      userId: achievement.userId,
      visibilityType: visibility || achievement.visibilityType
    };
    return http.put(`${ACHIEVEMENTS_API}/${achievement.publicId}`, {
      data: putData
    }).then(() => {
      return produce(achievement, draft => {
        draft.metadata = Object.assign({}, draft.metadata, {}, metadata);

        if (visibility) {
          draft.private = visibility !== AWARD_VISIBILITIES.PUBLIC;
          draft.visibilityType = visibility;
        }
      });
    });
  }

});

const updateAchievementsCache = achievement => {
  Object.keys(achievement).forEach(publicId => {
    updateCache(GET_ACHIEVEMENT_BY_PUBLIC_ID, () => achievement[publicId], {
      publicId
    });
  });
  updateCache(GET_ALL_ACHIEVEMENTS, previousAchievements => {
    Object.keys(achievement).forEach(publicId => {
      previousAchievements[publicId] = achievement[publicId];
    });
  }, {});
};

export const getPublicAchievement = publicId => {
  if (!publicId) return null;
  return dataFetchingClient.queryUnwrapped({
    query: GET_ACHIEVEMENT_BY_PUBLIC_ID,
    variables: {
      publicId
    },
    fetchPolicy: 'cache-first'
  });
};
export const usePublicAchievement = publicId => {
  const {
    data: publicAchievement,
    error: publicAchievementError,
    loading: publicAchievementLoading
  } = useQuery(GET_ACHIEVEMENT_BY_PUBLIC_ID, {
    variables: {
      publicId
    },
    skip: !publicId,
    fetchPolicy: 'cache-first'
  });
  return {
    publicAchievement,
    publicAchievementError,
    publicAchievementLoading
  };
};
export const getAllAchievements = () => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_ALL_ACHIEVEMENTS
  }).then(achievements => {
    updateAchievementsCache(achievements);
    return achievements;
  });
};
export const useAllAchievements = () => {
  const {
    data: allAchievements,
    error: allAchievementsError,
    loading: allAchievementsLoading
  } = useQuery(GET_ALL_ACHIEVEMENTS, {
    onCompleted: data => {
      updateAchievementsCache(data.allAchievements);
    }
  });
  return {
    allAchievements,
    allAchievementsError,
    allAchievementsLoading
  };
};
export const getBrochureRoot = language => BROCHURE_URL_BY_LANGUAGE[language] || BROCHURE_URL_BY_LANGUAGE.en;
export const useAchievementByTrackId = trackId => {
  const {
    allAchievements,
    allAchievementsLoading,
    allAchievementsError
  } = useAllAchievements();
  const foundAchievement = useMemo(() => {
    return Object.values(allAchievements || {}).find(achievement => achievement.tracks[0].id === trackId);
  }, [allAchievements, trackId]);
  return {
    achievement: foundAchievement,
    achievementsLoading: allAchievementsLoading,
    achievementsError: allAchievementsError
  };
};
export const useAchievementTracks = publicId => {
  const {
    publicAchievement
  } = usePublicAchievement(publicId);
  if (!publicAchievement) return null;
  return publicAchievement.tracks;
};
export const useAchievementLessons = publicId => {
  const {
    publicAchievement
  } = usePublicAchievement(publicId);
  if (!publicAchievement) return null;
  return publicAchievement.tracks[0].lessons;
};
export const useAchievementUser = publicId => {
  const {
    publicAchievement
  } = usePublicAchievement(publicId);
  if (!publicAchievement) return null;
  return {
    userId: publicAchievement.userId,
    avatarHash: publicAchievement.avatarHash,
    firstName: publicAchievement.firstName,
    lastName: publicAchievement.lastName
  };
};
export const useAchievementBrochureUrl = (publicId, language) => {
  const tracks = useAchievementTracks(publicId);
  if (!tracks) return null;
  return tracks[0].metadata[language || getLang()].brochureUrl;
};
export const useAchievementCertificateUrl = publicId => {
  const tracks = useAchievementTracks(publicId);
  if (!tracks) return null;
  return tracks[0].award && tracks[0].award.certificate;
};
export const createAchievement = achievementData => {
  return dataFetchingClient.mutate({
    mutation: CREATE_ACHIEVEMENT,
    variables: achievementData,

    update(__, result) {
      if (!result.data) return;
      const createdAchievement = result.data.createAchievement;
      updateAchievementsCache(createdAchievement);
    }

  });
};
export const useCreateAchievement = achievementData => {
  const [mutate, {
    data: createAchievementData,
    loading: createAchievementLoading,
    error: createAchievementError,
    called: createAchievementCalled
  }] = useMutation(CREATE_ACHIEVEMENT, {
    variables: achievementData,

    update(__, result) {
      if (!result.data) return;
      const createdAchievement = result.data.createAchievement;
      updateAchievementsCache(createdAchievement);
    }

  });
  return [mutate, {
    createAchievementData,
    createAchievementLoading,
    createAchievementError,
    createAchievementCalled
  }];
};
export const updateAchievement = (achievement, metadata, visibility) => {
  return dataFetchingClient.mutate({
    mutation: UPDATE_ACHIEVEMENT,
    variables: {
      achievement,
      metadata,
      visibility
    },

    update(__, result) {
      if (!result.data) return;
      const updatedAchievement = result.data.updateAchievement;
      updateAchievementsCache({
        [updatedAchievement.publicId]: updatedAchievement
      });
    }

  });
};
const useAchievementUpdating = create(set => ({
  isUpdatingAchievement: {},
  setIsUpdatingAchievement: (publicId, isUpdatingAchievement) => set(produce(state => {
    state.isUpdatingAchievement[publicId] = isUpdatingAchievement;
  }))
}));
export const useUpdateAchievement = (achievement, metadata, visibility) => {
  const [mutate, {
    data: updateAchievementData,
    loading: updateAchievementLoading,
    error: updateAchievementError,
    called: updateAchievementCalled
  }] = useMutation(UPDATE_ACHIEVEMENT, {
    variables: {
      achievement: achievement,
      metadata,
      visibility
    },

    update(__, result) {
      if (!result.data) return;
      const updatedAchievement = result.data.updateAchievement;
      updateAchievementsCache({
        [updatedAchievement.publicId]: updatedAchievement
      });
    }

  });
  const {
    isUpdatingAchievement,
    setIsUpdatingAchievement
  } = useAchievementUpdating();
  const mutateAchievement = useCallback(() => {
    if (achievement) {
      setIsUpdatingAchievement(achievement.publicId, true);
      return mutate().then(data => {
        setIsUpdatingAchievement(achievement.publicId, false);
        return data;
      });
    }

    return null;
  }, [achievement, mutate, setIsUpdatingAchievement]);
  return [mutateAchievement, {
    updateAchievementData,
    updateAchievementLoading,
    updateAchievementGlobalLoading: achievement ? isUpdatingAchievement[achievement.publicId] : false,
    updateAchievementError,
    updateAchievementCalled
  }];
}; // TODO: Change how this is handled so we are not exporting plain functions. Perhaps a hook

export function getMetadataLanguageFallback(metadata, targetLanguage) {
  return metadata[targetLanguage] ? metadata[targetLanguage] : metadata[Object.keys(metadata)[0]];
}